import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCustomersQuery,
  useProductsQuery,
  useUpdateSaleMutation,
  type Sale,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatMoney, isoDate } from '@/lib/format';
import { Alert, Button, Input, Modal, Select, Textarea } from '@/components/ui';
import { Can } from '@/components/Can';
import { CustomerPicker } from '@/components/CustomerPicker';

interface LineDraft {
  variantId: string;
  quantity: string;
  unitPrice: string;
  discountValue: string;
}

/** Only a sale that has moved no stock can have its lines changed. */
const OPEN = ['DRAFT', 'PENDING'];

/**
 * Correcting a sale.
 *
 * While it is a draft, everything here is editable — nothing has happened yet.
 * Once it is confirmed the goods have left, the cost of them is recorded
 * against the lines and the money stands against the customer's name, so only
 * the things that *describe* the sale can be fixed. That is not a limitation
 * of the form: changing a confirmed line would put the sale and the stock
 * ledger into disagreement, and the ledger is the one that is right.
 */
export function SaleEditModal({
  sale,
  onClose,
  onSaved,
}: {
  sale: Sale;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const format = useFormat();
  const open = OPEN.includes(sale.status);

  const { data: customers } = useCustomersQuery({ limit: 200, isActive: true });
  const { data: products } = useProductsQuery(open ? { limit: 200 } : undefined, {
    skip: !open,
  });
  const [update, state] = useUpdateSaleMutation();

  const [form, setForm] = useState({
    customerId: sale.customer?.id ?? '',
    dueDate: sale.dueDate ? isoDate(new Date(sale.dueDate)) : '',
    notes: sale.notes ?? '',
    discountType: sale.discountType ?? '',
    discountValue: sale.discountValue ?? '0',
    shippingAmount: sale.shippingAmount ?? '0',
  });

  const [lines, setLines] = useState<LineDraft[]>(
    (sale.items ?? []).map((item) => ({
      variantId: item.variant.id,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      discountValue: item.discountValue ?? '0',
    })),
  );
  const [failure, setFailure] = useState<string | null>(null);

  const fieldErrors = apiFieldErrors(state.error);

  const variantOptions = (products?.items ?? []).flatMap((product) =>
    (product.variants ?? []).map((variant) => ({
      id: variant.id,
      label: `${product.name}${variant.name && variant.name !== product.name ? ` — ${variant.name}` : ''} (${variant.sku})`,
      price: variant.sellingPrice ?? product.sellingPrice,
    })),
  );

  const setLine = (index: number, changes: Partial<LineDraft>): void =>
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...changes } : line)),
    );

  const save = async (): Promise<void> => {
    setFailure(null);
    const filled = lines.filter((line) => line.variantId && Number(line.quantity) > 0);

    try {
      await update({
        id: sale.id,
        body: {
          customerId: form.customerId || undefined,
          dueDate: form.dueDate || null,
          notes: form.notes,
          ...(open
            ? {
                ...(form.discountType ? { discountType: form.discountType } : {}),
                discountValue: Number(form.discountValue || 0),
                shippingAmount: Number(form.shippingAmount || 0),
                lines: filled.map((line) => ({
                  variantId: line.variantId,
                  quantity: Number(line.quantity),
                  ...(line.unitPrice ? { unitPrice: Number(line.unitPrice) } : {}),
                  ...(Number(line.discountValue) > 0
                    ? { discountValue: Number(line.discountValue) }
                    : {}),
                })),
              }
            : {}),
        },
      }).unwrap();

      onSaved?.();
      onClose();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That sale could not be changed'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Edit ${sale.number}`}
      description={`${sale.status.toLowerCase().replace(/_/g, ' ')} · ${formatMoney(sale.total, format)}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={state.isLoading}>Cancel</Button>
          <Button
            loading={state.isLoading}
            disabled={open && lines.every((line) => !line.variantId)}
            onClick={() => void save()}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {failure && <Alert tone="danger">{failure}</Alert>}

        {!open && (
          <Alert tone="info">
            This sale is confirmed, so its lines and totals are fixed — the stock has moved and
            what it cost is recorded against them. Who it is for, when it is due and the notes can
            still be corrected. If the figures themselves are wrong, return the goods or delete the
            sale; both move the money back.
          </Alert>
        )}

        <CustomerPicker
          value={form.customerId}
          customers={customers?.items ?? []}
          onChange={(customerId) => setForm({ ...form, customerId })}
          error={fieldErrors.customerId}
          placeholder="Walk-in"
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Payment due"
            type="date"
            value={form.dueDate}
            onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
            hint="Leave empty for a sale that is not on credit."
          />
          {open && (
            <Input
              label="Delivery"
              type="number"
              min="0"
              step="0.01"
              value={form.shippingAmount}
              onChange={(event) => setForm({ ...form, shippingAmount: event.target.value })}
            />
          )}
        </div>

        {open && (
          <>
            <Can permission={P.SALE_DISCOUNT}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  label="Discount"
                  value={form.discountType}
                  onChange={(event) => setForm({ ...form, discountType: event.target.value })}
                >
                  <option value="">None</option>
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="FIXED">Fixed amount</option>
                </Select>
                <Input
                  label="Discount value"
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={!form.discountType}
                  value={form.discountValue}
                  onChange={(event) => setForm({ ...form, discountValue: event.target.value })}
                />
              </div>
            </Can>

            <div>
              <p className="mb-2 text-sm font-medium">Lines</p>
              <div className="space-y-3">
                {lines.map((line, index) => (
                  <div key={index} className="flex flex-wrap items-end gap-3">
                    <div className="min-w-44 flex-1">
                      <Select
                        label={index === 0 ? 'Product' : undefined}
                        value={line.variantId}
                        onChange={(event) => setLine(index, { variantId: event.target.value })}
                      >
                        <option value="">Select a product</option>
                        {variantOptions.map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="w-24">
                      <Input
                        label={index === 0 ? 'Qty' : undefined}
                        type="number"
                        min="0"
                        step="0.0001"
                        value={line.quantity}
                        onChange={(event) => setLine(index, { quantity: event.target.value })}
                      />
                    </div>
                    <div className="w-28">
                      <Input
                        label={index === 0 ? 'Price' : undefined}
                        type="number"
                        min="0"
                        step="0.0001"
                        value={line.unitPrice}
                        onChange={(event) => setLine(index, { unitPrice: event.target.value })}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={lines.length === 1}
                      onClick={() =>
                        setLines((current) => current.filter((_, position) => position !== index))
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() =>
                  setLines((current) => [
                    ...current,
                    { variantId: '', quantity: '1', unitPrice: '', discountValue: '0' },
                  ])
                }
              >
                Add a line
              </Button>
            </div>
          </>
        )}

        <Textarea
          label="Notes"
          rows={2}
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
        />
      </div>
    </Modal>
  );
}
